import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import type { Page } from '../../crm/client/pager'
import { fanLabel, toFan, toList, type Fan, type FanList } from '../../library/collections/collectionsData'
import { ConfirmPopover } from '../../library/ConfirmPopover'
import { ListLoading } from '../../library/ListLoading'
import { Sentinel } from '../../library/Sentinel'
import { useDebounced } from '../../library/useDebounced'
import { useMediaSrc } from '../../library/useMediaSrc'
import { usePagedList } from '../../library/usePagedList'
import type { VaultItem } from '../../library/vault/vaultData'
import { Avatar } from '../Avatar'
import { MEDIA_MAX, PRICE_MIN, type When } from '../home/homeData'
import { ContentPicker } from '../../library/picker/ContentPicker'
import { AmountDialog, ScheduleDialog } from '../home/Pickers'
import { OF, type OfApi } from '../ofApi'
import { buildMessageBody, EMPTY_MESSAGE, messageError, recipientsOf, type MessageDraft, type MessageMedia } from './messageData'
import { BusyLabel } from '../../components/Spinner'
import './messages.css'

export const REFERER = `${OF}/my/chats/send`
const PAGE = 20

type Tab = 'to' | 'exclude' | 'users'
type Raw = Record<string, unknown>
type Payload = { list?: Raw[]; hasMore?: boolean; nextOffset?: number }

interface Props {
  of: OfApi
  /** Keys the content picker's page cache. */
  accountId: string
  /** Vault files already attached (the Vault page's selection). */
  media?: MessageMedia[]
  onClose: () => void
  /** Queued on OnlyFans; how it goes out. */
  onSent: (raw: Record<string, unknown>, when: When) => void
}

const listId = (l: FanList) => l.id
const fanId = (f: Fan) => f.id

/**
 * OnlyFans' New message (/my/chats/send): who it goes to (Send to / Exclude
 * lists, or picked fans) beside the message itself (text, vault media, price,
 * schedule or save for later). One `POST messages/queue`.
 */
export function NewMessage({ of, accountId, media = EMPTY_MESSAGE.media, onClose, onSent }: Props) {
  const { t, i18n } = useTranslation('ofMessage')
  const [draft, setDraft] = useState<MessageDraft>(() => ({ ...EMPTY_MESSAGE, media }))
  const [tab, setTab] = useState<Tab>('to')
  const [q, setQ] = useState('')
  const query = useDebounced(q.trim())
  const [panel, setPanel] = useState<'vault' | 'price' | 'schedule' | null>(null)
  const [confirm, setConfirm] = useState<'send' | 'discard' | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const set = (patch: Partial<MessageDraft>) => {
    setError('')
    setDraft((d) => ({ ...d, ...patch }))
  }

  // OnlyFans' own lists for messaging (view=queue). "Recent…" needs a period
  // picker OnlyFans keeps in filters; it is left out here.
  const fetchLists = useCallback(
    async (offset: number): Promise<Page<FanList>> => {
      const p = await of.get<Payload>('lists', { view: 'queue', offset, skip_users: 'dups', limit: PAGE, format: 'infinite', query: query || null }, REFERER)
      const list = (p?.list ?? []).map(toList).filter((l) => l.id !== 'recent')
      return { list, hasMore: p?.hasMore === true, nextOffset: offset + (p?.list?.length ?? 0) }
    },
    [of, query],
  )
  const fetchFans = useCallback(
    async (offset: number): Promise<Page<Fan>> => {
      const p = await of.get<Payload>('subscriptions/subscribers', { offset, limit: PAGE, type: 'active', format: 'infinite', query: query || null }, REFERER)
      const list = (p?.list ?? []).map(toFan)
      return { list, hasMore: p?.hasMore === true, nextOffset: offset + list.length }
    },
    [of, query],
  )
  const lists = usePagedList(fetchLists, listId)
  const fans = usePagedList(tab === 'users' ? fetchFans : noFans, fanId)

  const toggleList = (key: 'lists' | 'excluded', id: string) =>
    set({ [key]: draft[key].includes(id) ? draft[key].filter((x) => x !== id) : [...draft[key], id] } as Partial<MessageDraft>)
  const toggleFan = (f: Fan) => {
    const on = draft.users.includes(f.id)
    set({ users: on ? draft.users.filter((x) => x !== f.id) : [...draft.users, f.id] })
  }
  const clearRecipients = () => set({ lists: [], excluded: [], users: [] })

  const who = recipientsOf(draft)
  const whoText = [
    who.lists ? t('to.lists', { count: who.lists }) : '',
    who.users ? t('to.fans', { count: who.users }) : '',
    draft.excluded.length ? t('to.excluding', { count: draft.excluded.length }) : '',
  ]
    .filter(Boolean)
    .join(' · ')
  const fmtWhen = (at: Date) => at.toLocaleString(i18n.language, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  const sendLabel = draft.when.kind === 'schedule' ? t('schedule') : draft.when.kind === 'later' ? t('saveLater') : t('send')
  const hasContent = draft.text.trim() !== '' || draft.media.length > 0

  const check = () => {
    const err = messageError(draft)
    if (err) {
      setError(t(`err.${err}`, { min: PRICE_MIN, max: MEDIA_MAX, extra: draft.media.length - MEDIA_MAX }))
      return false
    }
    return true
  }
  const send = async () => {
    setConfirm(null)
    setBusy(true)
    setError('')
    try {
      const raw = await of.send<Record<string, unknown>>('POST', 'messages/queue', REFERER, buildMessageBody(draft))
      onSent(raw ?? {}, draft.when)
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : t('error'))
    } finally {
      setBusy(false)
    }
  }
  // Lists can reach thousands of fans: OnlyFans asks first, and so do we.
  const submit = () => check() && (draft.lists.length ? setConfirm('send') : void send())

  const when =
    draft.when.kind === 'schedule' ? t('confirm.at', { when: fmtWhen(draft.when.at) }) : draft.when.kind === 'later' ? t('confirm.later') : t('confirm.now')

  const rows =
    tab === 'users' ? (
      <>
        {fans.items.map((f) => (
          <label key={f.id} className="ofm-row">
            <input type="checkbox" className="ofh-pick-box" checked={draft.users.includes(f.id)} onChange={() => toggleFan(f)} />
            <Avatar user={{ name: fanLabel(f), avatar: f.avatar }} size={36} />
            <span className="ofm-row-text">
              <span className="ofm-row-name">{fanLabel(f)}</span>
              <span className="ofm-row-sub">@{f.username}</span>
            </span>
          </label>
        ))}
        {fans.hasMore && !fans.failed && <Sentinel onVisible={fans.loadMore} token={fans.items.length} />}
        <ListLoading loading={fans.loading} empty={!fans.items.length} />
        {!fans.loading && !fans.hasMore && !fans.items.length && <p className="lib-empty">{t('to.noFans')}</p>}
      </>
    ) : (
      <>
        {/* A list sits on one side only: the other tab's picks are left out here. */}
        {lists.items.filter((l) => !(tab === 'to' ? draft.excluded : draft.lists).includes(l.id)).map((l) => {
          const key = tab === 'to' ? 'lists' : 'excluded'
          return (
            <label key={l.id} className="ofm-row">
              <input type="checkbox" className="ofh-pick-box" checked={draft[key].includes(l.id)} onChange={() => toggleList(key, l.id)} />
              <span className="ofm-row-text">
                <span className="ofm-row-name">{l.name}</span>
                <span className="ofm-row-sub">{t('to.users', { count: l.users })}</span>
              </span>
              <span className="ofm-row-prev" aria-hidden="true">
                {l.previews.map((p) => (
                  <Avatar key={p.id} url={p.url} user={{ name: p.name, avatar: p.url }} size={24} />
                ))}
              </span>
            </label>
          )
        })}
        {lists.hasMore && !lists.failed && <Sentinel onVisible={lists.loadMore} token={lists.items.length} />}
        <ListLoading loading={lists.loading} empty={!lists.items.length} />
        {!lists.loading && !lists.hasMore && !lists.items.length && <p className="lib-empty">{t('to.noLists')}</p>}
      </>
    )

  return (
    <section className="ofm" aria-label={t('title')}>
      <header className="ofh-head">
        <div className="lib-anchor lib-anchor--start">
          <button type="button" className="lib-icon-btn" aria-label={t('back')} onClick={() => (hasContent || who.any ? setConfirm('discard') : onClose())}>
            <FeatherIcon name="arrow-left" size={20} />
          </button>
          {confirm === 'discard' && (
            <ConfirmPopover title={t('discardTitle')} body={t('discardBody')} confirmLabel={t('discard')} onConfirm={onClose} onCancel={() => setConfirm(null)} />
          )}
        </div>
        <h1 className="ofh-title">{t('title')}</h1>
        <span className="lib-spacer" />
        <div className="lib-anchor">
          <button type="button" className="lib-btn lib-btn--primary ofh-post-btn" disabled={busy} onClick={submit}>
            <BusyLabel busy={busy} label={t('sending')}>{sendLabel}</BusyLabel>
          </button>
          {confirm === 'send' && (
            <ConfirmPopover
              title={t('confirm.title', { who: whoText })}
              body={when}
              confirmLabel={sendLabel}
              onConfirm={() => void send()}
              onCancel={() => setConfirm(null)}
            />
          )}
        </div>
      </header>

      <div className="ofm-body">
        <aside className="ofm-to" aria-label={t('to.title')}>
          {who.any || draft.excluded.length ? (
            <div className="ofm-who">
              <FeatherIcon name="users" size={14} />
              <span className="ofm-who-text">{whoText}</span>
              <button type="button" className="ofh-setting-x" aria-label={t('to.clear')} onClick={clearRecipients}>
                <FeatherIcon name="x" size={12} />
              </button>
            </div>
          ) : null}
          <input className="ofp-input ofm-search" type="search" aria-label={t('to.search')} placeholder={t('to.search')} value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="ofm-tabs" role="tablist" aria-label={t('to.title')}>
            {(['to', 'exclude', 'users'] as const).map((k) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} className={`ofm-tab ${tab === k ? 'is-on' : ''}`} onClick={() => setTab(k)}>
                {t(`to.tab.${k}`)}
              </button>
            ))}
          </div>
          <div className="ofm-rows" role="group" aria-label={t(`to.tab.${tab}`)}>
            {rows}
          </div>
        </aside>

        <div className="ofm-compose">
          <div className="ofh-settings">
            {draft.when.kind === 'schedule' && (
              <Chip icon="calendar" text={fmtWhen(draft.when.at)} onOpen={() => setPanel('schedule')} onClear={() => set({ when: { kind: 'now' } })} />
            )}
            {draft.when.kind === 'later' && <Chip icon="calendar" text={t('laterChip')} onOpen={() => setPanel('schedule')} onClear={() => set({ when: { kind: 'now' } })} />}
            {draft.price > 0 && <Chip icon="dollar-sign" text={t('priceChip', { price: draft.price.toFixed(2) })} onOpen={() => setPanel('price')} onClear={() => set({ price: 0 })} />}
          </div>
          {draft.media.length > 0 && (
            <div className="ofh-attached" aria-label={t('media')}>
              {draft.media.map((m) => (
                <Attached key={m.id} item={m} onRemove={() => set({ media: draft.media.filter((x) => x.id !== m.id) })} />
              ))}
            </div>
          )}
          <textarea className="ofh-editor" aria-label={t('text')} placeholder={t('placeholder')} value={draft.text} rows={4} onChange={(e) => set({ text: e.target.value })} />
          <div className="ofh-tools" role="toolbar" aria-label={t('tools')}>
            <ToolButton icon="folder" label={t('tool.vault')} on={draft.media.length > 0} onClick={() => setPanel('vault')} />
            <ToolButton icon="calendar" label={t('tool.schedule')} on={draft.when.kind !== 'now'} onClick={() => setPanel('schedule')} />
            <ToolButton icon="dollar-sign" label={t('tool.price')} on={draft.price > 0} onClick={() => setPanel('price')} />
          </div>
          {error && (
            <p className="ofh-error" role="alert">
              {error}
            </p>
          )}
        </div>
      </div>

      {panel === 'vault' && (
        <ContentPicker accountId={accountId} initial={draft.media} max={MEDIA_MAX} onDone={(m: VaultItem[]) => (set({ media: m }), setPanel(null))} onClose={() => setPanel(null)} />
      )}
      {panel === 'price' && (
        <AmountDialog kind="price" title={t('priceTitle')} value={draft.price || null} onSave={(v) => (set({ price: v ?? 0 }), setPanel(null))} onClose={() => setPanel(null)} />
      )}
      {panel === 'schedule' && (
        <ScheduleDialog
          title={t('scheduleTitle')}
          laterNote={t('laterNote')}
          value={draft.when.kind === 'schedule' ? draft.when.at : null}
          onSchedule={(at) => (set({ when: { kind: 'schedule', at } }), setPanel(null))}
          onLater={() => (set({ when: { kind: 'later' } }), setPanel(null))}
          onClose={() => setPanel(null)}
        />
      )}
    </section>
  )
}

const noFans = async (): Promise<Page<Fan>> => ({ list: [], hasMore: false })

function ToolButton({ icon, label, on, onClick }: { icon: string; label: string; on: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`ofh-tool ${on ? 'is-on' : ''}`} aria-label={label} title={label} aria-pressed={on} onClick={onClick}>
      <FeatherIcon name={icon} size={19} />
    </button>
  )
}

function Chip({ icon, text, onOpen, onClear }: { icon: string; text: string; onOpen: () => void; onClear: () => void }) {
  const { t } = useTranslation('ofMessage')
  return (
    <span className="ofh-setting">
      <button type="button" className="ofh-setting-open" onClick={onOpen}>
        <FeatherIcon name={icon} size={13} />
        {text}
      </button>
      <button type="button" className="ofh-setting-x" aria-label={t('clear', { what: text })} onClick={onClear}>
        <FeatherIcon name="x" size={12} />
      </button>
    </span>
  )
}

function Attached({ item, onRemove }: { item: MessageMedia; onRemove: () => void }) {
  const { t } = useTranslation('ofMessage')
  const { ref, src } = useMediaSrc(item.thumb ?? '')
  return (
    <span ref={ref as React.RefObject<HTMLSpanElement>} className="ofh-att">
      {src ? <img src={src} alt="" /> : <FeatherIcon name={item.type === 'video' ? 'play' : 'image'} size={18} />}
      {item.type === 'video' && (
        <span className="ofh-att-play" aria-hidden="true">
          <FeatherIcon name="play" size={14} />
        </span>
      )}
      <button type="button" className="ofh-att-x" aria-label={t('removeMedia')} onClick={onRemove}>
        <FeatherIcon name="x" size={12} />
      </button>
    </span>
  )
}

